<template>
  <header class="home-nav">
    <div class="nav-left">
      <button
        v-if="showMenuButton"
        class="menu-toggle"
        type="button"
        :class="{ open: menuOpen }"
        aria-label="Menu"
        @click="$emit('toggle-menu')"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      <div class="brand-block">
        <img v-if="siteLogoUrl" class="brand-logo" :src="$resolveUrl(siteLogoUrl)" :alt="siteName" />
        <div class="brand-copy">
          <span v-if="!siteLogoUrl" ref="brandNameRef" class="brand-name" :class="{ 'is-overflow': brandNameOverflow }">{{ siteName }}</span>
          <span v-else class="brand-name brand-name--with-logo">{{ siteName }}</span>
          <span v-if="logoText" class="nav-tagline">{{ logoText }}</span>
        </div>
      </div>
    </div>

    <div class="nav-right" ref="navRightRef">
      <!-- Modo IA -->
      <button
        v-if="showIaButton"
        class="ia-pill-btn"
        :class="{ 'ia-pill-btn--active': iaMode }"
        type="button"
        aria-label="Modo IA"
        @click="$emit('ia-mode')"
      >
        <svg class="ia-sparkle" width="16" height="16" viewBox="0 0 24 24" fill="none">
          <defs>
            <linearGradient id="ia-icon-grad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%"   stop-color="#818cf8"/>
              <stop offset="50%"  stop-color="#34d399"/>
              <stop offset="100%" stop-color="#fb923c"/>
            </linearGradient>
          </defs>
          <path d="M12 2l1.5 4.5L18 8l-4.5 1.5L12 14l-1.5-4.5L6 8l4.5-1.5L12 2z" fill="url(#ia-icon-grad)"/>
          <path d="M19 14l.75 2.25L22 17l-2.25.75L19 20l-.75-2.25L16 17l2.25-.75L19 14z" fill="url(#ia-icon-grad)" opacity=".8"/>
        </svg>
        <span class="ia-pill-label">Modo IA</span>
      </button>

      <!-- Offline indicator -->
      <div v-if="isOffline && isOfflineModeEnabled()" class="offline-nav-badge">
        <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M1 1l18 18M16.72 11.06A10.94 10.94 0 0 1 19 12.55M5 12.55a10.94 10.94 0 0 1 5.17-2.39M10.71 5.05A16 16 0 0 1 22.56 9M1.42 9a15.91 15.91 0 0 1 4.7-2.88M8.53 16.11a6 6 0 0 1 6.95 0M12 20h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        Offline
      </div>

      <!-- What's New badge -->
      <button
        v-if="versionStore.hasNewVersion.value"
        class="nav-action-btn whats-new-btn"
        type="button"
        aria-label="Novidades da versão"
        @click="$emit('whats-new')"
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
        </svg>
        <span class="whats-new-dot"></span>
      </button>

      <button
        class="nav-action-btn notify-btn"
        type="button"
        aria-label="Notificacoes"
        @click="$emit('notifications')"
      >
        <span class="notification-icon" :class="{ 'has-alert': notificationCount > 0 }"></span>
        <span v-if="notificationCount > 0" class="notify-badge">{{ notificationBadgeText }}</span>
      </button>

      <button
        class="user-area"
        type="button"
        @click="toggleUserMenu"
      >
        <span class="user-name">{{ userName }}</span>
        <span class="avatar" :class="{ 'has-img': !!userAvatar }">
          <img v-if="userAvatar" :src="userAvatar" alt="perfil" />
          <span v-else class="avatar-initials">{{ userInitials }}</span>
        </span>
      </button>

      <div v-if="showUserMenu" class="user-menu">
        <div class="um-profile">
          <div class="um-avatar">
            <img v-if="userAvatar" :src="userAvatar" alt="perfil" />
            <span v-else class="um-avatar-initials">{{ userInitials }}</span>
          </div>
          <div class="um-identity">
            <span class="um-name">{{ userName }}</span>
            <span class="um-username">@{{ userUsername }}</span>
          </div>
        </div>
        <div class="user-menu-divider"></div>
        <button class="user-menu-item" type="button" @click="$emit('profile'); showUserMenu = false">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.582-7 8-7s8 3 8 7"/></svg>
          {{ $t('nav.settingsMenu') }}
        </button>
        <button class="user-menu-item" type="button" @click="$emit('anotacoes'); showUserMenu = false">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
          Anotações
        </button>
        <button v-if="isInstallable && !isInstalled" class="user-menu-item user-menu-item--install" type="button" @click="promptInstall(); showUserMenu = false">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M12 8v8M8 12l4 4 4-4"/></svg>
          Instalar como app
        </button>
        <div v-if="isInstalled" class="user-menu-item user-menu-item--installed" style="cursor:default;opacity:.6">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          App instalado
        </div>
        <button class="user-menu-item" type="button" @click="showInfoModal = true; showUserMenu = false">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="8.5" stroke-width="2.5" stroke-linecap="round"/><line x1="12" y1="12" x2="12" y2="16" stroke-linecap="round"/></svg>
          Mais informações
        </button>
        <div class="user-menu-divider"></div>
        <button class="user-menu-item user-menu-item--danger" type="button" @click="handleLogout">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          {{ $t('common.logout') }}
        </button>
      </div>

      <!-- ══ MODAL: Mais informações ══ -->
      <Teleport to="body">
        <div v-if="showInfoModal" class="info-overlay" @click.self="showInfoModal = false">
          <div class="info-modal">
            <div class="info-modal-header">
              <div class="info-modal-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="8.5" stroke-width="2.5" stroke-linecap="round"/><line x1="12" y1="12" x2="12" y2="16" stroke-linecap="round"/></svg>
              </div>
              <h3 class="info-modal-title">Sobre este sistema</h3>
              <button class="info-modal-close" type="button" @click="showInfoModal = false">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div class="info-modal-body">
              <p>Respondendo uma dúvida que você talvez tenha: eu, Gustavo, fiz esse sistema do zero. Não é um sistema de terceiros pirateado, é um sistema autoral que vem sendo atualizado com o feedback de cada um de vocês!</p>
              <p>Esse sistema vem sendo desenhado com o intuito de ajudar a todos nós para que cresçamos juntos como equipe e por esse motivo o feedback de vocês é muito importante!</p>
            </div>
            <div class="info-modal-footer">
              <button class="info-modal-btn" type="button" @click="showInfoModal = false">Entendido!</button>
            </div>
          </div>
        </div>
      </Teleport>
    </div>
  </header>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { isOffline, isOfflineModeEnabled } from '@/composables/useOfflineMode'
import { versionStore } from '@/store/versionStore'
import { isInstallable, isInstalled, promptInstall } from '@/store/installPromptStore'

export default defineComponent({
  name: 'DashboardNavbar',
  props: {
    siteName: {
      type: String,
      default: 'Obra Tycoon'
    },
    siteLogoUrl: {
      type: String,
      default: ''
    },
    searchQuery: {
      type: String,
      default: ''
    },
    userName: {
      type: String,
      required: true
    },
    userAvatar: {
      type: String,
      default: ''
    },
    notificationCount: {
      type: Number,
      default: 0
    },
    showMenuButton: {
      type: Boolean,
      default: false
    },
    menuOpen: {
      type: Boolean,
      default: false
    },
    logoText: {
      type: String,
      default: 'ObraTycoon by GusMachado'
    },
    userUsername: {
      type: String,
      default: ''
    },
    iaMode: {
      type: Boolean,
      default: false
    },
    showIaButton: {
      type: Boolean,
      default: true
    }
  },
  emits: ['profile', 'logout', 'toggle-menu', 'search-change', 'search-submit', 'notifications', 'whats-new', 'ia-mode', 'anotacoes'],
  setup(props, { emit }) {
    const showUserMenu = ref(false)
    const showInfoModal = ref(false)
    const navRightRef = ref<HTMLElement | null>(null)
    const brandNameRef = ref<HTMLElement | null>(null)
    const brandNameOverflow = ref(false)
    const localSearchQuery = ref(props.searchQuery)

    let brandObserver: ResizeObserver | null = null

    const userInitials = computed(() => {
      const name = props.userName || ''
      const parts = name.trim().split(' ')
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
      return name.slice(0, 2).toUpperCase()
    })

    const toggleUserMenu = () => {
      showUserMenu.value = !showUserMenu.value
    }

    const onSearchModelUpdate = (value: string) => {
      localSearchQuery.value = value
      emit('search-change', value)
    }

    const submitSearch = () => {
      emit('search-submit', localSearchQuery.value)
    }

    const handleGlobalClick = (event: MouseEvent) => {
      const target = event.target as Node
      const clickedUser = navRightRef.value && navRightRef.value.contains(target)
      if (!clickedUser) showUserMenu.value = false
    }

    onMounted(() => {
      window.addEventListener('click', handleGlobalClick)
      if (brandNameRef.value) {
        const checkOverflow = () => {
          const el = brandNameRef.value
          if (el) brandNameOverflow.value = el.scrollWidth > el.clientWidth
        }
        checkOverflow()
        brandObserver = new ResizeObserver(checkOverflow)
        brandObserver.observe(brandNameRef.value)
      }
    })

    onBeforeUnmount(() => {
      window.removeEventListener('click', handleGlobalClick)
      brandObserver?.disconnect()
    })

    watch(() => props.searchQuery, (value) => {
      localSearchQuery.value = value
    })

    watch(() => props.siteName, () => {
      const el = brandNameRef.value
      if (el) brandNameOverflow.value = el.scrollWidth > el.clientWidth
    })

    const notificationBadgeText = computed(() =>
      props.notificationCount > 9 ? '+9' : String(props.notificationCount)
    )

    const handleLogout = () => {
      if (isOffline.value && isOfflineModeEnabled()) {
        const ok = window.confirm(
          'Você está offline.\n\nSe deslogar agora não conseguirá entrar novamente até ter conexão com a internet.\n\nDeseja continuar?'
        )
        if (!ok) return
      }
      showUserMenu.value = false
      emit('logout')
    }

    return {
      showUserMenu,
      showInfoModal,
      navRightRef,
      brandNameRef,
      brandNameOverflow,
      localSearchQuery,
      notificationBadgeText,
      userInitials,
      toggleUserMenu,
      onSearchModelUpdate,
      submitSearch,
      handleLogout,
      isOffline,
      isOfflineModeEnabled,
      versionStore,
      isInstallable,
      isInstalled,
      promptInstall,
    }
  }
})
</script>

<style scoped lang="scss">
.home-nav {
  height: var(--navbar-h);
  min-height: var(--navbar-h);
  max-height: var(--navbar-h);
  background: var(--system-navbar, #0f4c81);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px 0 0;
  position: sticky;
  top: 0;
  z-index: 100;
  gap: 8px;
}

/* ── LEFT ── */
.nav-left {
  display: flex;
  align-items: center;
  gap: 0;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  padding: 0 12px;
}

.menu-toggle {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 4px;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  margin-right: 6px;
  flex-shrink: 0;
  transition: background 0.12s ease;
}

.menu-toggle:hover {
  background: rgba(255, 255, 255, 0.1);
}

.menu-toggle span {
  width: 18px;
  height: 1.5px;
  background: var(--system-navbar-text, #ffffff);
  border-radius: 2px;
  display: block;
  transition: transform 0.2s ease, opacity 0.15s ease;
}

.menu-toggle.open span:nth-child(1) {
  transform: translateY(5.5px) rotate(45deg);
}

.menu-toggle.open span:nth-child(2) {
  opacity: 0;
}

.menu-toggle.open span:nth-child(3) {
  transform: translateY(-5.5px) rotate(-45deg);
}

.brand-block {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  overflow: hidden;
  flex: 1;
}

.brand-copy {
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
  overflow: hidden;
}

.brand-logo {
  max-height: 28px;
  max-width: 160px;
  object-fit: contain;
}

.brand-name {
  font-size: 15px;
  font-weight: 700;
  color: var(--system-navbar-text, #ffffff);
  white-space: nowrap;
  letter-spacing: 0.01em;
  overflow: hidden;
  min-width: 0;
  flex-shrink: 1;
}

.brand-name--with-logo {
  font-size: 14px;
}

.brand-name.is-overflow {
  -webkit-mask-image: linear-gradient(to right, black calc(100% - 36px), transparent 100%);
  mask-image: linear-gradient(to right, black calc(100% - 36px), transparent 100%);
}

.nav-tagline {
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.55);
  white-space: nowrap;
  flex-shrink: 0;
  padding-left: 8px;
  border-left: 1px solid rgba(255, 255, 255, 0.16);
  letter-spacing: 0.01em;
}

/* ── RIGHT ── */
.nav-right {
  display: flex;
  align-items: center;
  gap: 4px;
  position: relative;
  flex-shrink: 0;
}

.nav-action-btn {
  width: 34px;
  height: 34px;
  border-radius: 4px;
  border: none;
  background: transparent;
  color: var(--system-navbar-text, #ffffff);
  display: grid;
  place-items: center;
  cursor: pointer;
  position: relative;
  transition: background 0.12s ease;
}

.nav-action-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.whats-new-btn {
  color: #fbbf24;
}

.whats-new-dot {
  position: absolute;
  top: 5px;
  right: 5px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #f59e0b;
  border: 1.5px solid var(--system-navbar, #0f4c81);
  animation: wn-pulse 2s infinite;
}

@keyframes wn-pulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50%       { transform: scale(1.25); opacity: 0.7; }
}

.notification-icon {
  width: 18px;
  height: 18px;
  display: block;
  background-color: var(--system-navbar-text, #ffffff);
  mask-image: url('@/assets/notification.svg');
  mask-mode: alpha;
  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: contain;
  -webkit-mask-image: url('@/assets/notification.svg');
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
  -webkit-mask-size: contain;
}

.notify-badge {
  position: absolute;
  top: 3px;
  right: 3px;
  min-width: 16px;
  height: 16px;
  border-radius: 8px;
  background: #d93025;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  line-height: 1;
}

.nav-text-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: var(--system-navbar-text, #ffffff);
  border-radius: 4px;
  padding: 5px 12px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
}

/* ── IA BUTTON ── */
.ia-pill-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 13px 6px 10px;
  border-radius: 50px;
  border: 2px solid transparent;
  background:
    linear-gradient(#fff, #fff) padding-box,
    linear-gradient(90deg, #818cf8 0%, #34d399 45%, #fbbf24 75%, #fb923c 100%) border-box;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: #18181b;
  letter-spacing: 0.01em;
  flex-shrink: 0;
  transition: box-shadow 0.18s, transform 0.15s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 14px rgba(99, 102, 241, 0.22);
  }

  &:active {
    transform: translateY(0);
  }

  &--active {
    background:
      linear-gradient(rgba(241,240,255,0.9), rgba(241,240,255,0.9)) padding-box,
      linear-gradient(90deg, #818cf8 0%, #34d399 45%, #fbbf24 75%, #fb923c 100%) border-box;
    box-shadow: 0 0 0 3px rgba(129, 140, 248, 0.15), 0 2px 12px rgba(99,102,241,0.18);
  }
}

.ia-sparkle {
  flex-shrink: 0;

  .ia-pill-btn--active & {
    animation: ia-sparkle-pulse 2s ease-in-out infinite;
  }
}

@keyframes ia-sparkle-pulse {
  0%, 100% { transform: scale(1) rotate(0deg); }
  50%       { transform: scale(1.15) rotate(12deg); }
}

.ia-pill-label {
  line-height: 1;
  color: #18181b;
}

/* ── USER AREA ── */
.user-area {
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px 8px 4px 6px;
  border-radius: 4px;
  margin-left: 4px;
  transition: background 0.12s ease;
}

.user-area:hover {
  background: rgba(255, 255, 255, 0.1);
}

.user-name {
  font-size: 13px;
  font-weight: 500;
  color: var(--system-navbar-text, #ffffff);
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  overflow: hidden;
  display: grid;
  place-items: center;
  flex-shrink: 0;
}

.avatar.has-img {
  background: transparent;
}

.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-initials {
  font-size: 11px;
  font-weight: 700;
  color: var(--system-navbar-text, #ffffff);
  letter-spacing: 0.02em;
}

/* ── USER MENU DROPDOWN ── */
.user-menu {
  position: absolute;
  top: calc(var(--navbar-h) - 4px);
  right: 0;
  background: var(--surface, #fff);
  border: 1px solid var(--border, rgba(31,30,26,.12));
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,.18), 0 2px 8px rgba(0,0,0,.10);
  min-width: 280px;
  overflow: hidden;
  z-index: 200;
}

/* Profile block */
.um-profile {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 16px 14px;
}

.um-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--accent, #0f4c81);
  overflow: hidden;
  display: grid;
  place-items: center;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.um-avatar-initials {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.02em;
}

.um-identity {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.um-name {
  font-size: 14px;
  font-weight: 700;
  color: var(--ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.um-username {
  font-size: 12px;
  color: var(--ink-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-menu-divider {
  height: 1px;
  background: var(--border, rgba(31,30,26,.1));
  margin: 0;
}

.user-menu-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: transparent;
  border: none;
  text-align: left;
  cursor: pointer;
  font-size: 13px;
  color: var(--ink);
  transition: background 0.1s ease;

  &:hover {
    background: rgba(31,30,26,.05);
  }

  &--danger {
    color: #dc2626;
    &:hover { background: rgba(220,38,38,.07); }
  }

  &--install {
    color: var(--accent, #0078d4);
    font-weight: 600;
    &:hover { background: rgba(0,120,212,.07); }
  }
}

/* ── INFO MODAL ── */
.info-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.45);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.info-modal {
  background: var(--bg, #f7f4ee);
  border-radius: 16px;
  width: min(500px, 100%);
  box-shadow: 0 16px 56px rgba(0,0,0,.28);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.info-modal-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 20px 16px;
  background: var(--surface, #fff);
  border-bottom: 1px solid rgba(31,30,26,.09);
}

.info-modal-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: rgba(15,76,129,.1);
  color: var(--accent, #0f4c81);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.info-modal-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--ink);
  flex: 1;
  margin: 0;
}

.info-modal-close {
  background: none;
  border: none;
  color: var(--ink-muted);
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover { background: rgba(31,30,26,.08); }
}

.info-modal-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.65;
    color: var(--ink);
  }
}

.info-modal-footer {
  padding: 14px 20px;
  display: flex;
  justify-content: flex-end;
  background: var(--surface, #fff);
  border-top: 1px solid rgba(31,30,26,.09);
}

.info-modal-btn {
  padding: 9px 22px;
  background: var(--accent, #0f4c81);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;

  &:hover { opacity: .88; }
}

/* ── RESPONSIVE ── */
@media (max-width: 900px) {
  .home-nav {
    padding: 0 10px 0 0;
  }

  .nav-left {
    min-width: 0;
    padding: 0 8px;
  }

  .brand-name {
    font-size: 14px;
  }

  .user-name {
    display: none;
  }

  .nav-tagline {
    display: none;
  }
}

.offline-nav-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: var(--system-navbar-text, #fff);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  padding: 3px 9px;
  border-radius: 20px;
  white-space: nowrap;
  opacity: 0.9;
}
</style>
